import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { PublicConfig } from '@jordan/types';
import { apiGet } from '../lib/api';

interface ConfigContextValue extends PublicConfig {
  refresh: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

const DEFAULT: PublicConfig = {
  appName: 'Jordan',
  timezone: 'America/New_York',
  copyright: '© {year} Jordan',
  hasLogo: false,
  logoUpdatedAt: null,
};

/** Loads public branding config (app name, logo) and exposes a refresh for after edits. */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<PublicConfig>(DEFAULT);

  const refresh = useCallback(async () => {
    try {
      setCfg(await apiGet<PublicConfig>('/api/config/public'));
    } catch {
      setCfg(DEFAULT);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <ConfigContext.Provider value={{ ...cfg, refresh }}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigContextValue {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
}
